/**
 * Contratos
 *
 * Gestao de contratos com cards por cliente, barra de consumo,
 * alertas de repactuacao e modal de detalhe com aditivos.
 */

import { useState, useMemo, useCallback } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/stores/page-title-store'
import { SensitiveValue } from '@/components/privacy/SensitiveText'
import {
  Search, X, ChevronDown, Check,
  FileText, Calendar, AlertTriangle, Upload,
  Clock, TrendingUp, Plus, HelpCircle,
} from 'lucide-react'
import {
  MOCK_CONTRATOS,
  type Contrato,
  type ContratoStatus,
  type Aditivo,
} from './mock-data'
import { MOCK_NOTAS } from '../central-de-notas/mock-data'

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_LABELS: Record<ContratoStatus, string> = {
  ativo: 'Ativo',
  encerrado: 'Encerrado',
  'em-negociacao': 'Em Negociacao',
}

const STATUS_COLORS: Record<ContratoStatus, string> = {
  ativo: 'bg-brand-primary/15 text-brand-primary border-brand-primary/30',
  encerrado: 'bg-transparent text-brand-gray border-brand-gray/40',
  'em-negociacao': 'bg-yellow-400/15 text-yellow-400 border-yellow-400/30',
}

const FORMA_ICONS: Record<string, string> = {
  'Fee Mensal': 'Receita recorrente fixa por mes',
  'Success Fee': 'Remuneracao atrelada a resultados',
  'Misto': 'Combinacao de fee fixo + variavel',
  'Projeto': 'Valor fechado por entrega/projeto',
}

// ============================================================================
// HELPERS
// ============================================================================

function formatCurrencyBR(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function formatCompact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`
  if (abs >= 1_000) return `R$ ${(value / 1_000).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}K`
  return formatCurrencyBR(value)
}

function formatDateBR(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

function formatMonthYear(iso: string): string {
  const [y, m] = iso.split('-')
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  return `${months[parseInt(m, 10) - 1]}/${y.slice(2)}`
}

function diffMonths(start: string, end: string): number {
  const [sy, sm] = start.split('-').map(Number)
  const [ey, em] = end.split('-').map(Number)
  return (ey - sy) * 12 + (em - sm)
}

function daysUntil(iso: string): number {
  const target = new Date(iso)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.floor((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

/** Compute consumption metrics for a contract. */
function computeConsumo(contrato: Contrato) {
  const notasContrato = MOCK_NOTAS.filter(
    (n) => n.contrato === contrato.numero && n.status !== 'cancelada'
  )
  const valorEmitido = notasContrato.reduce((s, n) => s + n.valorBruto, 0)
  const valorLimiteTotal = contrato.valorLimite +
    contrato.aditivos.reduce((s, a) => s + (a.valorAdicional ?? 0), 0)
  const valorLimite = valorLimiteTotal / (contrato.agenciasEnvolvidas || 1)
  const percentualUsado = valorLimite > 0 ? (valorEmitido / valorLimite) * 100 : 0
  const valorRestante = Math.max(0, valorLimite - valorEmitido)

  const todayISO = new Date().toISOString().split('T')[0]
  const mesesDecorridos = Math.max(1, diffMonths(contrato.periodo.inicio, todayISO))
  const mesesRestantes = Math.max(0, diffMonths(todayISO, contrato.periodo.fim))
  const mesesTotal = diffMonths(contrato.periodo.inicio, contrato.periodo.fim)

  const projecaoLinear = mesesRestantes > 0 ? valorRestante / mesesRestantes : 0
  const ritmoMensal = valorEmitido / mesesDecorridos
  const risco = mesesRestantes > 0 && ritmoMensal > projecaoLinear
  const projecaoFinal = ritmoMensal * mesesTotal
  const percentualProjetado = valorLimite > 0 ? (projecaoFinal / valorLimite) * 100 : 0

  return {
    notasCount: notasContrato.length,
    valorEmitido,
    valorLimite,
    percentualUsado,
    valorRestante,
    mesesDecorridos,
    mesesRestantes,
    projecaoLinear,
    ritmoMensal,
    risco,
    projecaoFinal,
    percentualProjetado,
  }
}

// ============================================================================
// COMPONENTS
// ============================================================================

// ---------------------------------------------------------------------------
// Contrato Detail Modal
// ---------------------------------------------------------------------------

function ContratoDetailModal({
  contrato,
  onClose,
}: {
  contrato: Contrato
  onClose: () => void
}) {
  const consumo = computeConsumo(contrato)
  const diasRepactuacao = contrato.dataRepactuacao ? daysUntil(contrato.dataRepactuacao) : null
  const barColor =
    consumo.percentualUsado > 95 ? 'bg-red-400' :
    consumo.percentualUsado > 80 ? 'bg-yellow-400' :
    'bg-brand-primary'

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-[10vh] px-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#1E2024] border border-[#2A2D34] rounded-2xl w-full max-w-2xl shadow-2xl mb-10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#2A2D34]">
          <div className="flex items-center gap-3 min-w-0">
            <FileText size={20} className="text-brand-primary shrink-0" />
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-white truncate">
                Contrato {contrato.numero}
              </h2>
              <p className="text-sm text-brand-gray">{contrato.cliente}</p>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border shrink-0 ${STATUS_COLORS[contrato.status]}`}>
              {STATUS_LABELS[contrato.status]}
            </span>
          </div>
          <button onClick={onClose} className="text-brand-gray hover:text-white transition-colors cursor-pointer p-1">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-6">
          {/* Dados do contrato */}
          <div>
            <h3 className="text-xs font-semibold text-brand-gray uppercase tracking-wider mb-3">Dados do Contrato</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
              <Field label="Cliente" value={contrato.cliente} />
              <Field label="N. Contrato" value={contrato.numero} />
              <Field label="Periodo" value={`${formatMonthYear(contrato.periodo.inicio)} → ${formatMonthYear(contrato.periodo.fim)}`} />
              <Field label="Valor Total" value={<SensitiveValue mask="R$ ••••••">{formatCurrencyBR(contrato.valorLimite + contrato.aditivos.reduce((s, a) => s + (a.valorAdicional ?? 0), 0))}</SensitiveValue>} />
              <Field label="Agências" value={String(contrato.agenciasEnvolvidas)} />
              <Field label="Expectativa Monumenta" value={<SensitiveValue mask="R$ ••••••">{formatCurrencyBR(consumo.valorLimite)}</SensitiveValue>} />
              <Field label="Forma de Remuneracao" value={contrato.formaRemuneracao} />
              {contrato.percentuaisRemuneracao && (
                <Field
                  label="Percentuais"
                  value={Object.entries(contrato.percentuaisRemuneracao)
                    .filter(([, v]) => v != null)
                    .map(([k, v]) => `${k}: ${v}%`)
                    .join(' · ')}
                />
              )}
              {contrato.dataRepactuacao && (
                <Field
                  label="Repactuacao"
                  value={
                    <span className="flex items-center gap-1.5">
                      {formatDateBR(contrato.dataRepactuacao)}
                      {diasRepactuacao != null && diasRepactuacao <= 60 && (
                        <span className="text-[10px] text-red-400 font-semibold">
                          ({diasRepactuacao <= 0 ? 'vencida' : `${diasRepactuacao}d`})
                        </span>
                      )}
                    </span>
                  }
                />
              )}
              <Field label="PDF" value={contrato.pdfUrl ? 'Disponivel' : 'Nao anexado'} />
            </div>
          </div>

          {/* Barra de consumo */}
          <div>
            <h3 className="text-xs font-semibold text-brand-gray uppercase tracking-wider mb-3">Execucao</h3>
            <div className="metric-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white font-semibold">
                  <SensitiveValue mask="R$ ••••••">{formatCompact(consumo.valorEmitido)}</SensitiveValue>
                  {' '}de{' '}
                  <SensitiveValue mask="R$ ••••••">{formatCompact(consumo.valorLimite)}</SensitiveValue>
                </span>
                <span className={`text-sm font-bold ${consumo.percentualUsado > 95 ? 'text-red-400' : consumo.percentualUsado > 80 ? 'text-yellow-400' : 'text-brand-primary'}`}>
                  {consumo.percentualUsado.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-brand-dark-lighter overflow-hidden">
                <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${Math.min(100, consumo.percentualUsado)}%` }} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <MiniStat label="Disponivel" value={<SensitiveValue mask="R$ ••••">{formatCompact(consumo.valorRestante)}</SensitiveValue>} />
                <MiniStat label="Ritmo atual" value={<SensitiveValue mask="R$ ••••">{formatCompact(consumo.ritmoMensal)}</SensitiveValue>} sub="/mes" />
                <MiniStat label="Proj. linear" value={<SensitiveValue mask="R$ ••••">{formatCompact(consumo.projecaoLinear)}</SensitiveValue>} sub="/mes" />
                <MiniStat
                  label="Projecao final"
                  value={`${consumo.percentualProjetado.toFixed(0)}%`}
                  alert={consumo.risco}
                />
              </div>
            </div>
          </div>

          {/* Aditivos */}
          {contrato.aditivos.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-brand-gray uppercase tracking-wider mb-3">
                Aditivos ({contrato.aditivos.length})
              </h3>
              <div className="space-y-2">
                {contrato.aditivos.map((aditivo) => (
                  <div key={aditivo.id} className="metric-card !p-3">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-white">{aditivo.numero}</span>
                          <span className="text-[10px] text-brand-gray">{formatDateBR(aditivo.data)}</span>
                        </div>
                        <p className="text-xs text-brand-gray/80 mt-0.5 truncate">{aditivo.descricao}</p>
                      </div>
                      {aditivo.valorAdicional && (
                        <span className="text-xs font-semibold text-brand-primary shrink-0 ml-3">
                          <SensitiveValue mask="R$ ••••">+{formatCompact(aditivo.valorAdicional)}</SensitiveValue>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-brand-gray/60 text-xs">{label}</span>
      <div className="text-white text-sm mt-0.5">{value}</div>
    </div>
  )
}

function MiniStat({ label, value, sub, alert }: { label: string; value: React.ReactNode; sub?: string; alert?: boolean }) {
  return (
    <div>
      <span className="text-brand-gray/60 text-[10px] uppercase tracking-wide">{label}</span>
      <div className={`text-sm font-semibold mt-0.5 ${alert ? 'text-red-400' : 'text-white'}`}>
        {value}
        {sub && <span className="text-brand-gray/50 font-normal text-[10px]">{sub}</span>}
        {alert && <AlertTriangle size={11} className="inline ml-1 -mt-0.5" />}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Contrato Card
// ---------------------------------------------------------------------------

function ContratoCard({
  contrato,
  onClick,
}: {
  contrato: Contrato
  onClick: () => void
}) {
  const consumo = computeConsumo(contrato)
  const diasRepactuacao = contrato.dataRepactuacao ? daysUntil(contrato.dataRepactuacao) : null
  const repactuacaoUrgente = diasRepactuacao != null && diasRepactuacao <= 60
  const barColor =
    consumo.percentualUsado > 95 ? 'bg-red-400' :
    consumo.percentualUsado > 80 ? 'bg-yellow-400' :
    'bg-brand-primary'

  return (
    <div
      className="metric-card cursor-pointer hover:border-brand-primary/30 transition-colors group"
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-white truncate group-hover:text-brand-primary transition-colors">
            {contrato.cliente}
          </h3>
          <p className="text-[10px] text-brand-gray mt-0.5">
            Contrato {contrato.numero} · {formatMonthYear(contrato.periodo.inicio)} → {formatMonthYear(contrato.periodo.fim)}
          </p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border shrink-0 ${STATUS_COLORS[contrato.status]}`}>
          {STATUS_LABELS[contrato.status]}
        </span>
      </div>

      {/* Barra de consumo */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-brand-gray/60">
            <SensitiveValue mask="R$ ••••">{formatCompact(consumo.valorEmitido)}</SensitiveValue>
            {' / '}
            <SensitiveValue mask="R$ ••••">{formatCompact(consumo.valorLimite)}</SensitiveValue>
          </span>
          <span className={`font-semibold ${consumo.percentualUsado > 95 ? 'text-red-400' : consumo.percentualUsado > 80 ? 'text-yellow-400' : 'text-brand-primary'}`}>
            {consumo.percentualUsado.toFixed(1)}%
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-brand-dark-lighter overflow-hidden">
          <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${Math.min(100, consumo.percentualUsado)}%` }} />
        </div>
      </div>

      {/* Metrics row */}
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-brand-gray/60">
          {contrato.formaRemuneracao}
          {contrato.agenciasEnvolvidas > 1 && ` · ${contrato.agenciasEnvolvidas} agências`}
        </span>
        <div className="flex items-center gap-3">
          {consumo.risco && (
            <span className="flex items-center gap-0.5 text-red-400 font-semibold">
              <AlertTriangle size={10} /> Ritmo acima
            </span>
          )}
          {contrato.aditivos.length > 0 && (
            <span className="text-brand-gray/50">{contrato.aditivos.length} aditivo{contrato.aditivos.length > 1 ? 's' : ''}</span>
          )}
          {repactuacaoUrgente && (
            <span className="flex items-center gap-0.5 text-red-400 font-semibold">
              <Clock size={10} />
              {diasRepactuacao! <= 0 ? 'Repact. vencida' : `Repact. ${diasRepactuacao}d`}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

function ContratosFilterBar({
  statusFilter,
  onStatusToggle,
  searchQuery,
  onSearchChange,
}: {
  statusFilter: Set<ContratoStatus>
  onStatusToggle: (s: ContratoStatus) => void
  searchQuery: string
  onSearchChange: (q: string) => void
}) {
  const statuses: ContratoStatus[] = ['ativo', 'em-negociacao', 'encerrado']

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
      {/* Search */}
      <div className="relative flex-1 w-full sm:max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-gray/50" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Buscar cliente ou contrato..."
          className="w-full bg-brand-dark-lighter border border-brand-dark-lighter rounded-lg pl-9 pr-8 py-2 text-xs text-white placeholder-brand-gray/40 focus:outline-none focus:border-brand-primary/50"
        />
        {searchQuery && (
          <button onClick={() => onSearchChange('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-brand-gray/50 hover:text-white cursor-pointer">
            <X size={13} />
          </button>
        )}
      </div>

      {/* Status chips */}
      <div className="flex items-center gap-1.5">
        {statuses.map((s) => {
          const active = statusFilter.size === 0 || statusFilter.has(s)
          return (
            <button
              key={s}
              onClick={() => onStatusToggle(s)}
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wide transition-colors cursor-pointer ${
                active
                  ? 'bg-brand-primary/20 text-brand-primary'
                  : 'text-brand-gray/40 hover:text-brand-gray hover:bg-brand-dark-lighter'
              }`}
            >
              {STATUS_LABELS[s]}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Summary Cards
// ---------------------------------------------------------------------------

function ContratosSummary({ contratos }: { contratos: Contrato[] }) {
  const stats = useMemo(() => {
    const ativos = contratos.filter((c) => c.status === 'ativo')
    const totalLimite = ativos.reduce((s, c) => {
      const aditivos = c.aditivos.reduce((sa, a) => sa + (a.valorAdicional ?? 0), 0)
      return s + (c.valorLimite + aditivos) / (c.agenciasEnvolvidas || 1)
    }, 0)
    const totalEmitido = ativos.reduce((s, c) => s + computeConsumo(c).valorEmitido, 0)
    const comRisco = ativos.filter((c) => computeConsumo(c).risco).length
    const repactuacaoProxima = ativos.filter((c) => {
      if (!c.dataRepactuacao) return false
      return daysUntil(c.dataRepactuacao) <= 60
    }).length

    return { totalAtivos: ativos.length, totalLimite, totalEmitido, comRisco, repactuacaoProxima }
  }, [contratos])

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div className="metric-card">
        <span className="text-[10px] text-brand-gray uppercase tracking-wide">Contratos Ativos</span>
        <div className="text-2xl font-bold font-display text-white mt-1">{stats.totalAtivos}</div>
      </div>
      <div className="metric-card">
        <span className="text-[10px] text-brand-gray uppercase tracking-wide">Limite Total</span>
        <div className="text-2xl font-bold font-display text-brand-primary mt-1">
          <SensitiveValue mask="R$ ••••••">{formatCompact(stats.totalLimite)}</SensitiveValue>
        </div>
      </div>
      <div className="metric-card">
        <span className="text-[10px] text-brand-gray uppercase tracking-wide">Com Risco de Estouro</span>
        <div className={`text-2xl font-bold font-display mt-1 ${stats.comRisco > 0 ? 'text-red-400' : 'text-white'}`}>{stats.comRisco}</div>
      </div>
      <div className="metric-card">
        <span className="text-[10px] text-brand-gray uppercase tracking-wide">Repactuacao Proxima</span>
        <div className={`text-2xl font-bold font-display mt-1 ${stats.repactuacaoProxima > 0 ? 'text-yellow-400' : 'text-white'}`}>{stats.repactuacaoProxima}</div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function Contratos() {
  usePageTitle('Contratos')
  const { user } = useAuth()

  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<Set<ContratoStatus>>(new Set())
  const [selectedContrato, setSelectedContrato] = useState<Contrato | null>(null)

  const toggleStatus = useCallback((s: ContratoStatus) => {
    setStatusFilter((prev) => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s)
      else next.add(s)
      return next
    })
  }, [])

  const filteredContratos = useMemo(() => {
    let data = [...MOCK_CONTRATOS]

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      data = data.filter(
        (c) => c.cliente.toLowerCase().includes(q) || c.numero.toLowerCase().includes(q)
      )
    }

    if (statusFilter.size > 0) {
      data = data.filter((c) => statusFilter.has(c.status))
    }

    // Sort: ativo first, then em-negociacao, then encerrado
    const statusOrder: Record<ContratoStatus, number> = { ativo: 0, 'em-negociacao': 1, encerrado: 2 }
    data.sort((a, b) => statusOrder[a.status] - statusOrder[b.status])

    return data
  }, [searchQuery, statusFilter])

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold font-display text-white">Contratos</h1>
          <p className="text-sm text-brand-gray mt-1">Gestao de contratos, aditivos e acompanhamento de execucao</p>
        </div>

        {/* Summary */}
        <ContratosSummary contratos={MOCK_CONTRATOS} />

        {/* Filters */}
        <div className="mb-5">
          <ContratosFilterBar
            statusFilter={statusFilter}
            onStatusToggle={toggleStatus}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </div>

        {/* Results count */}
        <p className="text-xs text-brand-gray/60 mb-4">
          {filteredContratos.length} contrato{filteredContratos.length !== 1 ? 's' : ''}
        </p>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredContratos.map((contrato) => (
            <ContratoCard
              key={contrato.id}
              contrato={contrato}
              onClick={() => setSelectedContrato(contrato)}
            />
          ))}
        </div>

        {filteredContratos.length === 0 && (
          <div className="text-center py-16">
            <FileText size={40} className="text-brand-gray/30 mx-auto mb-3" />
            <p className="text-brand-gray/60 text-sm">Nenhum contrato encontrado</p>
          </div>
        )}

      {/* Detail modal */}
      {selectedContrato && (
        <ContratoDetailModal
          contrato={selectedContrato}
          onClose={() => setSelectedContrato(null)}
        />
      )}
    </div>
  )
}
